import { useCallback, useEffect, useRef, useState } from "react";
import { watchAccessChanges } from "../services/accessChanges";

export const LIVE_ACCESS_INTERVAL = 5000;

export default function useSession(tokenKey, currentUser, { live = false } = {}) {
  const [user, updateUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const sequence = useRef(0);
  const pending = useRef(null);
  const dirty = useRef(false);
  const latest = useRef(null);
  const refreshLatest = useRef(null);
  latest.current = { user, loading, error };
  const setUser = useCallback(value => {
    sequence.current += 1;
    pending.current?.controller.abort(); pending.current = null; dirty.current = false;
    updateUser(value); setLoading(false); setError("");
  }, []);
  const retry = useCallback(async () => {
    const attempt = ++sequence.current;
    pending.current?.controller.abort(); pending.current = null; dirty.current = false;
    if (!localStorage.getItem(tokenKey)) {
      updateUser(null); setLoading(false); setError(""); return;
    }
    setLoading(true); setError("");
    try {
      const response = await currentUser();
      if (sequence.current === attempt) updateUser(response.data);
    } catch (failure) {
      if (sequence.current !== attempt) return;
      if ([401, 403].includes(failure.response?.status)) {
        localStorage.removeItem(tokenKey); updateUser(null);
      } else {
        setError("Unable to check your session. Please retry when the server is available.");
      }
    } finally {
      if (sequence.current === attempt) setLoading(false);
    }
  }, [tokenKey, currentUser]);
  const refreshAccess = useCallback(async () => {
    const previous = latest.current;
    if (!live || !previous?.user?.id || previous.loading || previous.error) return;
    if (pending.current) { dirty.current = true; return pending.current.promise; }
    const token = localStorage.getItem(tokenKey);
    if (!token) { setUser(null); return; }
    const attempt = sequence.current, identity = previous.user;
    const controller = new AbortController();
    const task = { controller, promise: null };
    pending.current = task;
    const active = () => !controller.signal.aborted && attempt === sequence.current && localStorage.getItem(tokenKey) === token;
    const deny = () => updateUser(value => ({ ...value, page_access: [], page_routes: value.page_routes || [], page_home: null, access_error: "Your access changed. Contact an administrator or check access again." }));
    task.promise = (async () => {
      try {
        const response = await currentUser({ signal: controller.signal, accessRefresh: true, timeout: 10000 });
        if (!active()) return;
        const fresh = response?.data;
        if (!fresh?.id || !fresh?.role) return; // A malformed read cannot replace a valid session.
        if (fresh.id !== identity.id || (identity.merchant_id && fresh.merchant_id !== identity.merchant_id)
            || (identity.merchant_code && fresh.merchant_code !== identity.merchant_code)) { deny(); return; }
        updateUser(value => JSON.stringify(value) === JSON.stringify(fresh) ? value : fresh);
      } catch (failure) {
        if (!active()) return;
        if (failure.response?.status === 401 || failure.response?.data?.detail?.code === "TENANT_MISMATCH") {
          localStorage.removeItem(tokenKey); setUser(null);
        } else if (failure.response?.status === 403) deny();
        // Transient errors keep the existing view and are retried in the background.
      } finally {
        if (pending.current === task) {
          pending.current = null;
          if (dirty.current && active()) { dirty.current = false; Promise.resolve().then(() => refreshLatest.current?.()); }
        }
      }
    })();
    return task.promise;
  }, [live, tokenKey, currentUser, setUser]);
  refreshLatest.current = refreshAccess;
  useEffect(() => { retry(); return () => { sequence.current += 1; pending.current?.controller.abort(); pending.current = null; }; }, [retry]);
  useEffect(() => {
    if (!live || loading || error || !user?.id) return undefined;
    const visible = () => document.visibilityState !== "hidden";
    const focused = () => { if (visible()) refreshAccess(); };
    const timer = setInterval(focused, LIVE_ACCESS_INTERVAL);
    const unwatch = watchAccessChanges(user.merchant_id || user.merchant_code || "", refreshAccess);
    const storage = event => { if (event.key === tokenKey) { if (!localStorage.getItem(tokenKey)) setUser(null); else retry(); } };
    window.addEventListener("focus", focused);
    window.addEventListener("storage", storage);
    document.addEventListener("visibilitychange", focused);
    return () => { clearInterval(timer); unwatch(); window.removeEventListener("focus", focused); window.removeEventListener("storage", storage); document.removeEventListener("visibilitychange", focused); };
  }, [live, loading, error, user?.id, user?.merchant_id, user?.merchant_code, tokenKey, refreshAccess, setUser, retry]);
  return { user, setUser, loading, error, retry, refreshAccess };
}
